import React, { useEffect, useState } from 'react'
import { IUser } from './types'
import { FaEdit, FaLock, FaUnlock } from 'react-icons/fa' 
import { IoKeyOutline } from "react-icons/io5"
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'
import DataTable, { TableColumn } from 'react-data-table-component'
import UserModal from './user-modal'
import ResetPasswordModal from './reset-password-modal'
import LockUnlockModal from './lock-unlock-modal'
import { Button, InputGroup, FormControl } from 'react-bootstrap'
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa"

const UserManagement: React.FC = () => {
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const [users, setUsers] = useState<IUser[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [modalVisible, setModalVisible] = useState<boolean>(false)
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState<boolean>(false)
    const [lockUnlockModalVisible, setLockUnlockModalVisible] = useState<boolean>(false) // New state for lock/unlock modal
    const [currentUser, setCurrentUser] = useState<IUser | null>(null) 
    const [isLocked, setIsLocked] = useState<boolean>(false) // Track if account is locked

    useEffect(() => {
        (async () => {
            try {
                const res = await doFetch('/user/get-users')
                if (res.status === 200) {
                    const data = await res.json()
                    setUsers(data)
                }
            } catch (err) {
                setError('Failed to load users.')
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    if (!user?.isAdmin) return null

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const columns: TableColumn<IUser>[] = [
        {
            selector: (row: IUser) => row.id,
            sortable: true,
            omit: true,
            id:'id',
        },
        {
            name: 'Username',
            selector: (row: IUser) => row.username,
            cell: (row: IUser) => <b>{row.username}</b>,
            sortable: true,
        },
        {
            name: 'Admin',
            selector: (row: IUser) => (row.isAdmin ? 'Yes' : 'No'),
            sortable: true,
        },
        {
            name: 'Kind',
            selector: (row: IUser) => row.kind,
            sortable: true,
        },
{
    name: 'Feed',
    cell: (row: IUser) => (
        row.feed?.disabled 
            ? <FaTimesCircle className="text-danger" size={14} /> 
            : <FaCheckCircle className="text-success" size={14} />
    ),
    sortable: true,
},
{
    name: 'Marketing',
    cell: (row: IUser) => (
        row.marketing?.disabled 
            ? <FaTimesCircle className="text-danger" size={14} /> 
            : <FaCheckCircle className="text-success" size={14} />
    ),
    sortable: true,
},
{
    name: "ByeLink",
    cell: (row: IUser) => (
        row.byClick?.enabled
            ? <FaCheckCircle className="text-success" size={14} />
            : <FaTimesCircle className="text-danger" size={14} />
    ),
    sortable: true,
},
        {
            name: 'Account Status',
            cell: (row: IUser) => (
                <span className={`badge ${row.loginAttempts && row.loginAttempts > 5 ? 'bg-danger' : 'bg-success'}`}>
                    {row.loginAttempts && row.loginAttempts > 5 ? 'Locked' : 'Active'}
                </span>
            ),
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row: IUser) => (
                <div>
                    <FaEdit
                        className="text-primary me-2"
                        style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                        onClick={() => handleEdit(row)}
                    />
                    {row.loginAttempts && row.loginAttempts > 5 ? (
                        <FaUnlock
                            className="text-success me-2"
                            style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                            onClick={() => handleLockUnlock(row, true)}
                        />
                    ) : (
                        <FaLock
                            className="text-danger me-2"
                            style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                            onClick={() => handleLockUnlock(row, false)}
                        />
                    )}
                    <IoKeyOutline
                        className="text-info"
                        style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                        onClick={() => handleResetPassword(row)}
                    />
                </div>
            ),
        },
    ]

    const handleEdit = (user: IUser) => {
        setCurrentUser(user)
        setModalVisible(true)
    }

    const handleCreateUser = () => {
        setCurrentUser(null)
        setModalVisible(true)
    }

    const handleLockUnlock = (user: IUser, unlock: boolean) => {
        setCurrentUser(user)
        setIsLocked(unlock)
        setLockUnlockModalVisible(true)
    }

    const handleResetPassword = (user: IUser) => {
        setCurrentUser(user)
        setResetPasswordModalVisible(true)
    }

    const handleLockUnlockSubmit = async (unlock: boolean) => {
        if (currentUser) {
            const kind = unlock ? 'unlock' : 'lock'
            const res = await doFetch(`/user/account-lock`, {
                username: currentUser.username,
                kind,
            })
            if (res.status !== 200) throw `Something went wrong: ${await res.text()}`

            const updatedUsers = users.map(user => {
                if (user.id === currentUser.id) {
                    return {
                        ...user,
                        loginAttempts: unlock ? 0 : 10,
                    }
                }
                return user
            })

            setUsers(updatedUsers)
        }
        setLockUnlockModalVisible(false)
    }

    const handlePasswordSubmit = async (newPassword: string) => {
        if (currentUser) {
            const res = await doFetch('/user/reset-password', {
                username: currentUser.username,
                password: newPassword,
            })
            if(res.status !== 200) throw `Something went wrong: ${await res.text()}`
        }
        setResetPasswordModalVisible(false)
    }

    const handleCloseModal = () => {
        setModalVisible(false)
        setCurrentUser(null)
    }

    if (loading) {
        return (
            <div className="container mt-4 d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <div className="container mt-4 text-danger">{error}</div>
    }

    return (
        <div className="container mt-4">
            <Button variant="primary" className="mb-3" onClick={handleCreateUser}>
                Create User
            </Button>

            <InputGroup className="mb-3">
                <FormControl
                    type="text"
                    placeholder="Search by username"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            <DataTable
                columns={columns}
                data={filteredUsers}
                pagination
                paginationPerPage={20}
                defaultSortFieldId="id"
                defaultSortAsc={false}
                striped
                highlightOnHover
                progressPending={loading}
                progressComponent={
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                }
            />

            <UserModal
                show={modalVisible}
                onHide={handleCloseModal}
                currentUser={currentUser}
                onSubmit={async (user, logo) => {
                    const url = currentUser ? '/user/update' : '/user/create'
                    
                    if (currentUser) (user as any).id = currentUser.id
                    console.log(user)
                    const formData = new FormData()
                    formData.append('logo', logo || '')
                    formData.append('user', JSON.stringify(user))
                    const res = await doFetch(url, formData)
                    if (res.status !== 200) throw await res.text()

                    const updatedUser: IUser = await res.json()
                    if (currentUser) {
                        const userIndex = users.findIndex((u) => u.id === currentUser.id)
                        users[userIndex] = { ...updatedUser, loginAttempts: currentUser.loginAttempts }
                    } else {
                        users.push(updatedUser)
                    }

                    setUsers(users)
                }}
            />

            <ResetPasswordModal
                show={resetPasswordModalVisible}
                onHide={() => setResetPasswordModalVisible(false)}
                onSubmit={handlePasswordSubmit}
            />

            <LockUnlockModal
                show={lockUnlockModalVisible}
                onHide={() => setLockUnlockModalVisible(false)}
                onSubmit={handleLockUnlockSubmit}
                isLocked={isLocked}
            />
        </div>
    )
}

export default UserManagement
